import type {
  ComponentType,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  UIEventHandler,
} from 'react';
import { cloneElement, forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SlideFadeProps } from '@chakra-ui/react';
import { Flex, FormLabel, Grid, IconButton, SlideFade, Text } from '@chakra-ui/react';

import { useAppHeight, useIsMobile } from '@izimi/hooks';

import { FeatherIcon } from '../icon';

const TypedSlideFade = SlideFade as ComponentType<PropsWithChildren<SlideFadeProps>>;

interface Props {
  title: string;
  subTitle: string;
  children: ReactNode;
  renderButtons: () => ReactNode;
  Illustration: ReactElement;
  step: number;
  onCancel: () => void;
}

export const WizardStep = forwardRef<HTMLDivElement | null, Props>(
  ({ title, subTitle, children, renderButtons, Illustration, step, onCancel }, ref) => {
    const [hasStartedScrolling, setHasStartedScrolling] = useState(false);
    const [hasScrollableContent, setHasScrollableContent] = useState(false);
    const appHeight = useAppHeight();
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    useEffect(() => {
      if (typeof ref !== 'function' && ref?.current) {
        setHasScrollableContent(ref.current.scrollHeight > ref.current.clientHeight);
      }
    }, [ref]);

    const onScroll: UIEventHandler<HTMLDivElement> = (e) => {
      if (!hasStartedScrolling && e.currentTarget.scrollTop > 0) {
        setHasStartedScrolling(true);
      }
      if (hasStartedScrolling && e.currentTarget.scrollTop === 0) {
        setHasStartedScrolling(false);
      }
    };

    const handleButtons = () => (
      <Flex
        position='sticky'
        zIndex='sticky'
        bottom={0}
        width='90%'
        borderTopWidth={1}
        borderColor='neutral200'
        mx='5%'
        backgroundColor='white'
        justifyContent='center'
        p={2}
        boxShadow={hasScrollableContent ? '0 -15px 15px -20px rgba(0,0,0,0.3)' : 'none'}>
        <Flex justifyContent='center' alignItems='center' flex={1}>
          {renderButtons()}
        </Flex>
      </Flex>
    );

    const currentStep = step;

    return (
      <Grid
        templateColumns={{ base: '1fr', md: '1.5fr 1fr' }}
        as='main'
        height={appHeight}
        overflow='hidden'>
        <Flex
          position='relative'
          direction='column'
          overflowY='auto'
          as='section'
          ref={ref}
          onScroll={onScroll}>
          <Text
            fontFamily='Yorkten Slab'
            fontSize={{ base: 64, md: 96 }}
            position={{ base: 'absolute', md: 'fixed' }}
            top='75px'
            left={{ base: -4, md: '-28px' }}
            color='primary100'
            zIndex={-1}
            aria-hidden='true'>
            0{currentStep}
          </Text>
          <Flex
            p={hasStartedScrolling ? 4 : 8}
            alignItems='center'
            position='fixed'
            transition='.2s'
            zIndex={1}>
            <IconButton
              backgroundColor='neutral100'
              variant='icon'
              aria-label='logout'
              onClick={onCancel}
              icon={<FeatherIcon name='x' />}
              title={t('action.cancel')}
              id='cancelButton'
              transition='.2s'
              size={hasStartedScrolling ? 'sm' : 'md'}
            />
            <FormLabel
              m={0}
              ml={1}
              p={1}
              fontFamily='Yorkten Slab'
              fontWeight={600}
              fontSize='16px'
              color='neutral400'
              htmlFor='cancelButton'
              cursor='pointer'
              background='rgba(255,255,255, 0.5)'
              transition='.1s'
              opacity={hasStartedScrolling ? 0 : 1}>
              {t('action.cancel')}
            </FormLabel>
          </Flex>
          <Flex
            width={{ base: '90%', md: '70%' }}
            maxWidth='550px'
            margin={{ base: '105px auto 0 auto', md: '135px auto 0 auto' }}
            direction='column'
            flexGrow={1}>
            <TypedSlideFade
              in
              style={{
                position: 'initial',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
              offsetY={100}>
              <Text variant='heading1' mb={{ base: 0, md: 2 }} ml={{ base: '40px', md: 0 }} as='h2'>
                {title}
              </Text>
              {cloneElement(Illustration as ReactElement<Record<string, unknown>>, {
                location: 'mobile',
              })}
              <Text variant='paragraph' color='neutral500' mb={6} as='h3'>
                {subTitle}
              </Text>
              <Flex pb={5} flexDirection='column' flex={1}>
                {children}
              </Flex>
            </TypedSlideFade>
          </Flex>
          {!isMobile && handleButtons()}
        </Flex>
        {isMobile && handleButtons()}
        {cloneElement(Illustration as ReactElement<Record<string, unknown>>, {
          location: 'desktop',
        })}
      </Grid>
    );
  },
);
